import React, { forwardRef, Ref, CSSProperties } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import isExternalLink from "utils/isExternalLink";
import LoadingSpinner from "components/Loaders/LoadingSpinner";
import { LoadingSpinnerSize } from "types/loadingSpinner";
import { ReactComponent as InfoIcon } from "static/icons/info-icon.svg";
import styles from "./Button.module.scss";
import SimpleToolTip from "components/Tooltip/SimpleToolTip";

type Props = {
    disabled?: boolean;
    onClick?: () => void;
    className?: string;
    children?: React.ReactNode;
    upperCase?: boolean;
    text?: string;
    to?: string;
    style?: CSSProperties;
    loading?: boolean;
    stopPropagation?: boolean;
    loadingSize?: LoadingSpinnerSize;
    toolTip?: string;
    variant?: "primary" | "secondary" | "outline-primary" | "light";
    size?: "extra-small" | "small" | "large" | "medium";
    id?: string;
};

const Button = forwardRef(
    (
        {
            stopPropagation,
            disabled,
            size = "large",
            variant = "primary",
            onClick,
            className,
            upperCase = true,
            text = "Save",
            children,
            to,
            style = {},
            loading,
            toolTip,
            loadingSize = LoadingSpinnerSize.small,
            id,
            ...rest
        }: Props & JSX.IntrinsicElements["button"],
        ref: Ref<HTMLButtonElement>,
    ) => {
        function handleClick(e: React.MouseEvent<HTMLElement>) {
            if (stopPropagation) {
                e.stopPropagation();
            }
            onClick && onClick();
        }

        const classes = classNames(
            "text-align-center",
            {
                [styles["button--large"]]: size === "large",
                [styles["button--small"]]: size === "small",
                [styles["button--medium"]]: size === "medium",
                [styles["button--extra-small"]]: size === "extra-small",
                [styles["button--primary"]]: variant === "primary",
                [styles["button--secondary"]]: variant === "secondary",
                [styles["button--light"]]: variant === "light",
                [styles["button--outline-primary"]]: variant === "outline-primary",
                [styles["button--uppercase"]]: upperCase,
                [styles["button--disabled"]]: disabled,
                [styles["button--as-link"]]: !!to,
            },
            className,
        );

        if (loading) {
            return (
                <button
                    disabled
                    style={style}
                    className={classNames(
                        classes,
                        styles["button--disabled"],
                        styles["button--loading"],
                    )}
                >
                    <LoadingSpinner size={loadingSize} />
                </button>
            );
        }

        if (to) {
            if (isExternalLink(to)) {
                return (
                    <a
                        target="_blank"
                        onClick={handleClick}
                        rel="noreferrer noopener"
                        className={classes}
                        href={to}
                        id={id}
                    >
                        <span className={styles["button__link-text"]}>{children || text}</span>
                    </a>
                );
            }
            return (
                <Link onClick={handleClick} className={classes} to={to}>
                    <span className={styles["button__link-text"]}>{children || text}</span>
                </Link>
            );
        }
        return toolTip ? (
            <div className={styles["button-content"]}>
                <button
                    ref={ref}
                    style={style}
                    disabled={disabled}
                    onClick={handleClick}
                    className={classes}
                    {...rest}
                >
                    {children || text}
                </button>
                <SimpleToolTip centered={false} content={toolTip}>
                    <InfoIcon className={styles["info-icon"]} />
                </SimpleToolTip>
            </div>
        ) : (
            <button
                ref={ref}
                style={style}
                disabled={disabled}
                onClick={handleClick}
                className={classes}
                {...rest}
            >
                {children || text}
            </button>
        );
    },
);

export default Button;
